import {
  Lead,
  GuideSection,
  P,
  UL,
  OL,
  LI,
  Term,
  GuideLink,
  Callout,
} from './guide-ui';

/**
 * Body for "MCP for security: connecting AI agents to your logs" guide.
 * Educational-first (what MCP is, the real risks), then LogPulse's design.
 */
export function McpForSecurityGuideBody() {
  return (
    <>
      <Lead>
        The Model Context Protocol (MCP) lets AI agents query your logs and security
        data directly, a huge productivity win, and a new attack surface. Connecting
        an agent to sensitive data raises real risks: prompt injection, tool
        poisoning, and over-broad permissions. This guide explains what MCP is, why
        it matters for security data, where the danger is, and how to connect agents
        safely.
      </Lead>

      <GuideSection id="what-is-mcp" title="What is the Model Context Protocol (MCP)?">
        <P>
          <Term>MCP</Term> is an open protocol that standardises how AI agents
          connect to external tools and data. Instead of scraping a UI or hand-
          writing queries, an agent connects to an MCP <Term>server</Term>,
          discovers a catalog of <Term>typed tools</Term>, and calls them to read
          data or take actions. It is becoming the common interface between agents
          (Claude Code, Cursor, Codex, and others) and the systems they work with.
        </P>
      </GuideSection>

      <GuideSection id="why-security" title="Why MCP matters for logs and security data">
        <P>
          For engineering and security teams, MCP means an analyst or developer can
          investigate logs, triage security, and build connectors from the agent
          they already use, without leaving their editor or exporting data. The
          agent calls typed tools (search logs, list notables, look up an IOC) that
          run <Term>inside</Term> the platform, so answers are fast, consistent,
          governed, and tenant-scoped, rather than the agent guessing from scraped
          screens.
        </P>
      </GuideSection>

      <GuideSection id="the-risks" title="The security risks of MCP">
        <P>
          Pointing an autonomous agent at your data introduces failure modes that
          traditional integrations do not have:
        </P>
        <UL>
          <LI><Term>Prompt injection</Term>: malicious instructions hidden in data the agent reads (including your own logs) try to hijack its behaviour.</LI>
          <LI><Term>Tool poisoning</Term>: malicious instructions embedded in a tool&apos;s metadata or description, so the agent is manipulated through a trusted interface.</LI>
          <LI><Term>Resource / context poisoning</Term>: hidden instructions planted in data sources or shared state that propagate to the agent as trusted information.</LI>
          <LI><Term>Excessive permissions</Term>: an over-scoped token or write-capable tool turns a compromised or confused agent into real damage.</LI>
        </UL>
        <Callout title="The core principle">
          <p>
            Treat everything the agent reads, log lines included, as{' '}
            <Term>data, not instructions</Term>, and give the agent the least
            privilege it needs. Most MCP incidents come down to one of those two
            principles being broken.
          </p>
        </Callout>
      </GuideSection>

      <GuideSection id="how-to-secure" title="How to connect agents to security data safely">
        <OL>
          <LI><Term>Read-only by default.</Term> Writes should be an explicit, narrow, opt-in allowlist, not the baseline.</LI>
          <LI><Term>Scoped, authenticated access.</Term> Per-token, capability-level scopes and authenticated connections (PAT or OAuth) so each agent sees only what it should.</LI>
          <LI><Term>Enforce existing RBAC.</Term> The agent should inherit the user&apos;s permissions, not bypass them.</LI>
          <LI><Term>Gateway, rate-limit, and audit.</Term> Route through a controlled endpoint, cap call rates, and log every call for review.</LI>
          <LI><Term>Approval workflows for change.</Term> Any action that modifies your environment should create a draft for human approval, not apply directly.</LI>
          <LI><Term>Treat tool output as data.</Term> Guard against instructions smuggled through retrieved content.</LI>
        </OL>
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="How LogPulse secures MCP access">
        <P>
          LogPulse ships a remote MCP server built on these principles. It is a
          single HTTPS endpoint authenticated by a scoped personal access token or
          OAuth 2.1. Tools are <Term>read-only by default</Term>, scoped per token
          (<Term>logs:read</Term>, <Term>siem:read</Term>, <Term>services:read</Term>,{' '}
          <Term>entities:read</Term>), and the only writes are opt-in{' '}
          <Term>propose</Term> scopes that queue a detection, rule, service, KPI or
          pipeline as a <Term>disabled draft for human approval</Term>. Nothing is
          applied directly.
        </P>
        <P>
          Every call is bound by the token holder&apos;s namespace RBAC, rate-limited
          and written to the audit log, and tool output is treated as data, not
          instructions, to guard against prompt injection through logs. Abnormal call
          volume or an agent write even raises a notable. Data stays EU-hosted. See
          the{' '}
          <GuideLink href="/mcp">MCP server</GuideLink> page and the{' '}
          <GuideLink href="/docs/integrations/mcp-server">MCP documentation</GuideLink>{' '}
          for the endpoint, scopes, and full tool catalog, and{' '}
          <GuideLink href="/guides/what-is-an-agentic-soc">what is an agentic SOC</GuideLink>{' '}
          for how this powers AI investigation with a human in the loop.
        </P>
      </GuideSection>
    </>
  );
}
